import type { PackageRow } from '@/services/api/packages';

import type {
  OutboundOrderFulfillment,
  OutboundOrderShippingLabel,
} from '../types';

// Shipping label of an order a third-party carrier took (Lion Parcel …). Those
// orders create no Dash shipment, so the Shipments label print has nothing to
// read; the BE builds the same content from the order instead
// (POST /outbound-orders/shipping-labels/bulk). Orders with a Dash shipment
// keep printing from the shipment (`useShipmentLabels().print`).

/**
 * Shipped with a 3PL: an AWB WMS issued and no Dash shipment. A handover has
 * no AWB, a Dash order has its shipment — neither prints from here.
 */
export const hasThirdPartyShippingLabel = (
  f: OutboundOrderFulfillment | null | undefined
): boolean =>
  !!f &&
  !f.shipmentID &&
  !!f.waybill &&
  !!f.carrier &&
  f.carrier !== 'DASH' &&
  f.carrier !== 'HANDOVER';

/**
 * The order has a shipping label: an AWB exists — its Dash shipment, or the
 * AWB WMS issued for a 3PL. From Submit shipping on; Ready to pick up has
 * no AWB yet, a handover never gets one.
 */
export const hasShippingLabel = (
  f: OutboundOrderFulfillment | null | undefined
): boolean => !!f?.shipmentID || hasThirdPartyShippingLabel(f);

/**
 * One label row per order line — the same fields `shipmentToPackageRows`
 * fills from a Dash shipment's items, so the printed label is identical.
 */
export const outboundLabelToPackageRows = (
  label: OutboundOrderShippingLabel
): PackageRow[] =>
  label.items.map((item, i) => ({
    id: `${label.outboundOrderID}:${item.sku}`,
    po: item.invoice,
    deliveryId: label.waybill,
    bookingId: label.bookingID || undefined,
    outlet: label.recipientName,
    sender: label.senderName,
    client: label.client
      ? { id: label.client.id, name: label.client.name }
      : undefined,
    qty: item.quantity,
    koli: item.koli,
    weight: item.weight,
    volume: 0,
    // A Dash shipment's items are born HUB_SCANNED at the hub; the label
    // doesn't print the status.
    status: 'HUB_SCANNED',
    recipientAddress: label.recipientAddress || undefined,
    senderAddress: label.senderAddress || undefined,
    itemName: item.sku,
    itemSeq: i + 1,
    itemTotal: label.items.length,
  }));
